import React from 'react';
import Container from '@v2/shared/components/Container';
import { useSite } from '@v2/shared/lib/site';
import type { FullLayoutProps } from '@v2/shared/lib/types';
import FooterMenu from '@v2/shared/Layouts/Full/Footer/FooterMenu';
import useCurrentYear from '@v2/shared/lib/useCurrentYear';

import AcceptedPaymentMethods from './AcceptedPaymentMethods';
import useFooterMenu from './useFooterMenu';

/**
 * Ported from `domain-overrides/magspot.com/layouts/full/footer.html`.
 *
 * Three columns on a grey band — the two link columns and, beside them, the copyright
 * and the card marks. The solid blue strip above them is the template's `bg-blue` row,
 * which holds nothing and is there to sit the footer off the page.
 *
 * The columns are drawn by the shared FooterMenu, which MagZoom's footer draws too. What
 * is MagSpot's is the list it hands over and the band around it.
 *
 * The copyright and the card marks only sit beside the menus from md. A third of the row
 * is 109px at sm, and the five card marks are 212px wide and do not wrap — so below md
 * they ran out of their column, over the Legal links and off the right edge of the page.
 * They take their own full-width row instead, which is what they already did under sm.
 *
 * Each menu is two rows tall, so About runs into two columns and Legal into three — the
 * shape the legacy footer arrived at by floating its items inside a third of the row, and
 * a footer half the height of five stacked links. That only fits from lg, where the row
 * stops being three equal thirds and becomes as wide as what is in it; below that the
 * columns are the thirds they were. MagZoom keeps a single column: three entries per menu
 * make a short one already, and its footer is tall regardless.
 */
/** The year comes from the layout's props — see `FullLayoutProps['copyrightYear']`. */
type Props = Pick<FullLayoutProps, 'copyrightYear'>;

export default function Footer({ copyrightYear }: Props) {
  const site = useSite();
  const menu = useFooterMenu();
  const year = useCurrentYear(copyrightYear);

  return (
    <footer className="mt-auto">
      <div className="h-12 bg-primary" />
      <div className="bg-slate-100">
        <Container className="py-6">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:flex lg:justify-between lg:gap-10">
            <FooterMenu groups={menu} rows={2} />

            <div className="col-span-2 text-center md:col-span-1">
              <p className="mb-2 text-xs text-slate-700">
                &copy; Copyright {year} {site.name}. All rights reserved.
              </p>
              <AcceptedPaymentMethods />
            </div>
          </div>
        </Container>
      </div>
    </footer>
  );
}
